/**
 * Bridges the host's bundled palettes to the xivcharakit `PaletteProvider` contract, so the library
 * interprets a `.chara` file against the same colors the grids show and no second corpus ships.
 *
 * Every request is validated here, before any data loads: the host getters pull whole tables and
 * `charaShaderHex` throws for a palette it does not know.
 */
import { charaShaderHex } from './chara-shader-colors.js';
/** A bridge identifier, not a game patch: the host cannot substantiate the library's metadata contract. */
const DATASET_VERSION = 'xivdyetools-host-palette-bridge/1';
const SHARED_SIZES = {
    eyes: 192,
    highlights: 192,
    features: 192,
    lipsDark: 96,
    lipsLight: 96,
    facePaintDark: 192,
    facePaintLight: 192,
};
const RACE_PALETTE_SIZE = 192;
// Plain arrays, not Sets: a top-level `new Set(...)` is kept by bundlers (not provably pure), which
// would keep this module in the web app's shared chunk even when the bridge is never imported.
const SHADER_PALETTES = [
    'eyes',
    'highlights',
    'features',
    'lipsDark',
    'skin',
    'hair',
];
const TRIBES = [
    'Midlander',
    'Highlander',
    'Wildwood',
    'Duskwight',
    'Plainsfolk',
    'Dunesfolk',
    'SeekerOfTheSun',
    'KeeperOfTheMoon',
    'SeaWolf',
    'Hellsguard',
    'Raen',
    'Xaela',
    'Helions',
    'TheLost',
    'Rava',
    'Veena',
];
const HOST_GENDER = { Masculine: 'Male', Feminine: 'Female' };
function colorFor(request, hex) {
    if (!hex || !/^#[0-9A-Fa-f]{6}$/.test(hex))
        return null;
    const upper = hex.toUpperCase();
    const n = parseInt(upper.slice(1), 16);
    const color = {
        table: request.table,
        palette: request.palette,
        index: request.index,
        hex: upper,
        rgb: [(n >> 16) & 255, (n >> 8) & 255, n & 255],
        datasetVersion: DATASET_VERSION,
    };
    if (request.palette === 'skin' || request.palette === 'hair') {
        color.tribe = request.tribe;
        color.gender = request.gender;
    }
    return color;
}
/** Creator-table hex for a shared palette, or undefined for a name the host does not hold. */
function sharedCreatorHex(colors, palette, index) {
    switch (palette) {
        case 'eyes':
            return colors.getEyeColors()[index]?.hex;
        case 'highlights':
            return colors.getHighlightColors()[index]?.hex;
        case 'features':
            return colors.getTattooColors()[index]?.hex;
        case 'lipsDark':
            return colors.getLipColorsDark()[index]?.hex;
        case 'lipsLight':
            return colors.getLipColorsLight()[index]?.hex;
        case 'facePaintDark':
            return colors.getFacePaintColorsDark()[index]?.hex;
        case 'facePaintLight':
            return colors.getFacePaintColorsLight()[index]?.hex;
        default:
            return undefined;
    }
}
export function createCharaPaletteProvider(colors) {
    return {
        async getColor(request) {
            const { table, palette, index } = request;
            if (table !== 'creator' && table !== 'shader')
                return null;
            if (typeof palette !== 'string' || !Number.isInteger(index) || index < 0)
                return null;
            if (palette === 'skin' || palette === 'hair') {
                if (index >= RACE_PALETTE_SIZE)
                    return null;
                const { tribe, gender } = request;
                if (typeof tribe !== 'string' || !TRIBES.includes(tribe))
                    return null;
                if (typeof gender !== 'string' || !Object.hasOwn(HOST_GENDER, gender))
                    return null;
                const hostTribe = tribe;
                const hostGender = HOST_GENDER[gender];
                if (table === 'shader') {
                    return colorFor(request, await charaShaderHex(palette, index, hostTribe, hostGender));
                }
                const list = palette === 'skin'
                    ? await colors.getSkinColors(hostTribe, hostGender)
                    : await colors.getHairColors(hostTribe, hostGender);
                return colorFor(request, list[index]?.hex);
            }
            const size = Object.hasOwn(SHARED_SIZES, palette) ? SHARED_SIZES[palette] : undefined;
            if (size === undefined || index >= size)
                return null;
            if (table === 'shader') {
                if (!SHADER_PALETTES.includes(palette))
                    return null;
                return colorFor(request, await charaShaderHex(palette, index, null, null));
            }
            return colorFor(request, sharedCreatorHex(colors, palette, index));
        },
    };
}
//# sourceMappingURL=chara-palette-provider.js.map